import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import HeroBanner from '../components/HeroBanner';
import ContentRow from '../components/ContentRow';
import CustomSectionModal from '../components/CustomSectionModal';
import { getContentByIds } from '../data/mockData';
import { fetchFirebaseContent, fetchTrendingContent, fetchMovies, fetchTVShows } from '../redux/slices/contentSlice';
import { addCustomSection, updateCustomSection, deleteCustomSection, reorderSections, loadCustomSections } from '../redux/slices/userSlice';
import { filterContent, sortContent, filterByMetadataQuality } from '../utils/contentFilter';

const HomeContainer = styled.div`
  min-height: 100vh;
  background-color: #141414;
`;

const ContentSection = styled.section`
  padding-bottom: 2rem;
`;

const AddSectionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 2px dashed #555;
  border-radius: 6px;
  color: #aaa;
  padding: 16px 24px;
  margin: 0 4% 2rem 4%;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  width: calc(100% - 8%);

  &:hover {
    border-color: #e50914;
    color: #fff;
    background: rgba(229, 9, 20, 0.1);
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    margin: 0 20px 1.5rem 20px;
    width: calc(100% - 40px);
    padding: 12px 20px;
  }
`;

const Home = () => {
  console.log('[Home] Component rendering');
  const dispatch = useDispatch();
  const { categories, scrapedContent, apiHealthy, isLoading } = useSelector(state => state.content);
  const { currentProfile } = useSelector(state => state.user);
  const { enabledSources, hideIncompleteMetadata } = useSelector(state => state.settings);

  // Custom sections state
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState(null);

  // Load custom sections from localStorage when profile changes
  useEffect(() => {
    if (currentProfile && currentProfile.id) {
      dispatch(loadCustomSections(currentProfile.id));
    }
  }, [currentProfile?.id, dispatch]);

  // Handlers for custom sections
  const handleCreateSection = () => {
    setEditingSectionId(null);
    setShowSectionModal(true);
  };

  const handleEditSection = (sectionId) => {
    setEditingSectionId(sectionId);
    setShowSectionModal(true);
  };

  const handleDeleteSection = (sectionId) => {
    dispatch(deleteCustomSection(sectionId));
  };

  const handleMoveSection = (sectionId, direction) => {
    dispatch(reorderSections({ sectionId, direction }));
  };

  const handleSaveSection = (formData) => {
    if (editingSectionId) {
      // Update existing section
      dispatch(updateCustomSection({
        sectionId: editingSectionId,
        updates: formData
      }));
    } else {
      // Create new section
      dispatch(addCustomSection(formData));
    }
    setShowSectionModal(false);
    setEditingSectionId(null);
  };

  // Filter content by enabled sources and metadata quality
  const filterByEnabledSources = useCallback((contentArray) => {
    if (!contentArray || contentArray.length === 0) return [];
    
    // First apply source filtering
    let filtered = contentArray.filter(item => {
      // Check if item has a source and if it's enabled
      if (!item.source) return true; // Include items without source
      return enabledSources.includes(item.source);
    });
    
    // Then apply metadata filtering if enabled
    if (hideIncompleteMetadata) {
      filtered = filterByMetadataQuality(filtered, true);
      console.log(`[Home] Metadata filtering: ${contentArray.length} → ${filtered.length} movies (${contentArray.length - filtered.length} hidden due to incomplete metadata)`);
    }
    
    return filtered;
  }, [enabledSources, hideIncompleteMetadata]);

  useEffect(() => {
    // Fetch content from Firebase (fast!) when component mounts
    console.log('[Home] Fetching content from Firebase...');

    dispatch(fetchFirebaseContent(500))  // Increased to 500 to get all movies with genres
      .unwrap()
      .then(movies => {
        console.log(`[Home] Successfully loaded ${movies.length} movies from Firebase`);

        // Fallback to live scraping if Firebase has insufficient data
        if (movies.length < 50) {
          console.log('[Home] Firebase has < 50 movies, falling back to scraping...');
          dispatch(fetchMovies());
        }
      })
      .catch(error => {
        console.error('[Home] Firebase fetch failed, falling back to scraping:', error);
        // Fallback to scraping on error
        dispatch(fetchMovies());
      });
  }, [dispatch]);

  // Helper function to shuffle and slice array
  const shuffleArray = (array, count = null) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return count ? shuffled.slice(0, count) : shuffled;
  };

  // Stable shuffle that only runs once per data set
  const [shuffledMovies, setShuffledMovies] = useState([]);
  const [shuffledTrending, setShuffledTrending] = useState([]);
  const [shuffledNewSources, setShuffledNewSources] = useState([]);
  const [shuffledAction, setShuffledAction] = useState([]);
  const [shuffledDramas, setShuffledDramas] = useState([]);
  const [shuffledSciFi, setShuffledSciFi] = useState([]);
  const [shuffledCrime, setShuffledCrime] = useState([]);
  const [shuffledPopular, setShuffledPopular] = useState([]);

  useEffect(() => {
    if (scrapedContent.movies?.length > 0) {
      // Filter by enabled sources, then shuffle
      const filtered = filterByEnabledSources(scrapedContent.movies);

      // All movies shuffled
      setShuffledMovies(shuffleArray(filtered));

      // Random selections for different categories
      setShuffledTrending(shuffleArray(filtered, 20));
      setShuffledNewSources(shuffleArray(filtered, 20));
      setShuffledAction(shuffleArray(filtered, 20));
      setShuffledDramas(shuffleArray(filtered, 20));
      setShuffledSciFi(shuffleArray(filtered, 20));
      setShuffledCrime(shuffleArray(filtered, 20));
      setShuffledPopular(shuffleArray(filtered, 10));
    }
  }, [scrapedContent.movies, enabledSources, hideIncompleteMetadata]);

  const personalizedRows = useMemo(() => {
    console.log('[Home] useMemo recalculating personalizedRows');
    // Debug logging
    console.log('API Health:', apiHealthy);
    console.log('Scraped Movies Length:', scrapedContent.movies?.length || 0);
    console.log('Enabled Sources:', enabledSources);

    // Base rows with scraped content and Continue Watching with mock data
    const rows = [
      {
        id: 'continue-watching',
        title: 'Continue Watching for ' + currentProfile?.name,
        content: getContentByIds(currentProfile?.continueWatching?.map(item => item.contentId) || []),
        useScraped: false
      },
      {
        id: 'trending-now',
        title: 'Trending Now',
        content: shuffledTrending,
        useScraped: true
      },
      {
        id: 'latest-movies',
        title: 'Latest Movies',
        content: shuffledMovies,
        useScraped: true
      },
      {
        id: 'new-sources',
        title: 'New Sources Collection',
        content: shuffledNewSources,
        useScraped: true
      },
      {
        id: 'action-adventure',
        title: 'Action & Adventure',
        content: shuffledAction,
        useScraped: true
      },
      {
        id: 'dramas',
        title: 'Dramas',
        content: shuffledDramas,
        useScraped: true
      },
      {
        id: 'sci-fi-movies',
        title: 'Sci-Fi Movies',
        content: shuffledSciFi,
        useScraped: true
      },
      {
        id: 'crime-thrillers',
        title: 'Crime Thrillers',
        content: shuffledCrime,
        useScraped: true
      },
      {
        id: 'popular-netflix',
        title: 'Popular on Netflix',
        content: shuffledPopular,
        useScraped: true
      }
    ];

    // Add custom sections from current profile
    if (currentProfile && currentProfile.customSections && currentProfile.customSections.length > 0) {
      // Build array of custom section rows
      const customSectionRows = [];

      currentProfile.customSections.forEach((section) => {
        // Filter content based on section's filters
        let filteredContent = filterContent(
          shuffledMovies,
          section.filters
        );

        // Sort content based on section's sorting preferences
        if (filteredContent.length > 0 && section.sortBy) {
          filteredContent = sortContent(filteredContent, section.sortBy, section.sortOrder);
        }

        // Debug logging for genre filtering
        console.log(`[CustomSection] "${section.title}" filters:`, section.filters);
        console.log(`[CustomSection] Sorted by: ${section.sortBy || 'default'} (${section.sortOrder || 'desc'})`);
        console.log(`[CustomSection] Filtered results: ${filteredContent.length} items`);
        if (filteredContent.length === 0 && section.filters.genres?.length > 0) {
          console.log('[CustomSection] Genre-only filter returned no results. Sample movie genres:',
            shuffledMovies.slice(0, 5).map(m => ({ title: m.title, genre: m.genre })));
        }

        if (filteredContent.length > 0) {
          customSectionRows.push({
            id: section.id,
            title: section.title,
            content: filteredContent,
            useScraped: true,
            isCustom: true,
            section: section  // Store full section data for edit
          });
        }
      });

      // Insert all custom sections at position 1 (after Continue Watching)
      rows.splice(1, 0, ...customSectionRows);
    }

    // Add dynamic source-specific categories
    enabledSources.forEach(source => {
      const sourceMovies = shuffledMovies.filter(m => m.source === source);
      if (sourceMovies.length > 0) {
        rows.push({
          id: `source-${source.toLowerCase()}`,
          title: `On ${source}`,
          content: sourceMovies,
          useScraped: true
        });
      }
    });

    // Add Live TV Channels section at the end
    const tvChannels = shuffledMovies.filter(m => m.type === 'tv_channel');
    if (tvChannels.length > 0) {
      rows.push({
        id: 'live-tv-channels',
        title: 'Live TV Channels',
        content: tvChannels,
        useScraped: true
      });
    }

    return rows.filter(row => row.content && row.content.length > 0);
  }, [shuffledTrending, shuffledMovies, shuffledNewSources, shuffledAction, shuffledDramas, shuffledSciFi, shuffledCrime, shuffledPopular, currentProfile?.name, currentProfile?.customSections, currentProfile?.continueWatching, enabledSources]);

  return (
    <HomeContainer>
      <HeroBanner />

      <ContentSection>
        {isLoading && (
          <div style={{ padding: '2rem 4%', color: '#808080' }}>
            Loading content from Firebase...
          </div>
        )}

        {/* Add New Section Button */}
        {currentProfile && !isLoading && (
          <AddSectionButton onClick={handleCreateSection}>
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            New Section
          </AddSectionButton>
        )}

        {personalizedRows.map((row, index) => {
          const customSectionIndex = currentProfile?.customSections?.findIndex(s => s.id === row.id) ?? -1;

          return (
            <ContentRow
              key={row.id}
              title={row.title}
              content={row.content}
              rowIndex={index}
              isCustom={row.isCustom || false}
              onEdit={row.isCustom ? () => handleEditSection(row.id) : undefined}
              onDelete={row.isCustom ? () => handleDeleteSection(row.id) : undefined}
              onMoveUp={row.isCustom && customSectionIndex > 0 ? () => handleMoveSection(row.id, 'up') : undefined}
              onMoveDown={row.isCustom && customSectionIndex < (currentProfile?.customSections?.length - 1) ? () => handleMoveSection(row.id, 'down') : undefined}
              canMoveUp={customSectionIndex > 0}
              canMoveDown={customSectionIndex >= 0 && customSectionIndex < (currentProfile?.customSections?.length - 1)}
            />
          );
        })}
      </ContentSection>

      {/* Custom Section Modal */}
      <CustomSectionModal
        isOpen={showSectionModal}
        onClose={() => {
          setShowSectionModal(false);
          setEditingSectionId(null);
        }}
        onSave={handleSaveSection}
        editingSection={editingSectionId ? currentProfile?.customSections?.find(s => s.id === editingSectionId) : null}
      />
    </HomeContainer>
  );
};

export default Home;